<template>
  <div class="invoices-tab">
    <div class="filters-section decaissement-filters-stacked">
      <div class="filters-header decaissement-filters-header">
        <h4>
          <a-icon type="filter" class="filter-icon" />
          {{ $t("all.filtresEtRecherche") }}
        </h4>
        <div class="action-button-group">
          <router-link to="/decaissement/nouvelle-depense">
            <a-button type="primary" icon="plus" class="action-button">
              {{ $t("factures.createNewDepense") }}
            </a-button>
          </router-link>
          <a-button class="action-button" @click="resetFilters">
            {{ $t("factures.resetFilters") }}
          </a-button>
          <a-button type="primary" class="action-button" @click="applyFilters">
            {{ $t("action.search") }}
          </a-button>
        </div>
      </div>

      <a-row :gutter="[16, 16]" type="flex" class="decaissement-filters-row">
        <a-col :xs="24" :sm="12" :md="12" :lg="8" class="filter-col">
          <div class="filter-item filter-item-stack">
            <label class="filter-label">
              <a-icon type="search" class="action-icon" />
              {{ $t("action.search") }}
            </label>
            <a-input
              v-model="filters.search"
              allow-clear
              class="filter-control"
              :placeholder="$t('factures.searchPlaceholder')"
              @pressEnter="applyFilters"
            >
              <a-icon slot="prefix" type="search" />
            </a-input>
          </div>
        </a-col>
        <a-col :xs="24" :sm="12" :md="12" :lg="8" class="filter-col">
          <div class="filter-item filter-item-stack">
            <label class="filter-label">
              <a-icon type="pie-chart" class="action-icon" />
              {{ $t("factures.filterInvoiceStatus") }}
            </label>
            <a-select
              v-model="filters.invoiceStatus"
              allow-clear
              class="filter-control"
              :placeholder="$t('factures.filterInvoiceStatus')"
              @change="applyFilters"
            >
              <a-select-option value="unpaid">{{ $t("fournisseur.unpaid") }}</a-select-option>
              <a-select-option value="partial">{{ $t("fournisseur.partial") }}</a-select-option>
              <a-select-option value="paid">{{ $t("fournisseur.paid") }}</a-select-option>
            </a-select>
          </div>
        </a-col>
        <a-col :xs="24" :sm="12" :md="12" :lg="8" class="filter-col">
          <div class="filter-item filter-item-stack">
            <label class="filter-label">
              <a-icon type="calendar" class="action-icon" />
              {{ $t("factures.filterSchedulingStatus") }}
            </label>
            <a-select
              v-model="filters.schedulingStatus"
              allow-clear
              class="filter-control"
              :placeholder="$t('factures.filterSchedulingStatus')"
              @change="applyFilters"
            >
              <a-select-option value="not_scheduled">{{ $t("fournisseur.notScheduled") }}</a-select-option>
              <a-select-option value="scheduled">{{ $t("fournisseur.scheduled") }}</a-select-option>
            </a-select>
          </div>
        </a-col>
        <a-col :xs="24" :sm="12" :md="12" :lg="12" class="filter-col">
          <div class="filter-item filter-item-stack">
            <label class="filter-label">
              <a-icon type="shop" class="action-icon" />
              {{ $t("factures.filterProvider") }}
            </label>
            <a-select
              v-model="filters.provider"
              show-search
              allow-clear
              class="filter-control"
              :filter-option="filterProviderOption"
              :placeholder="$t('factures.filterProvider')"
              :loading="loadingProviders"
              @change="applyFilters"
            >
              <a-select-option v-for="p in providers" :key="p._id" :value="p._id">
                {{ p.fullName || p.company || p._id }}
              </a-select-option>
            </a-select>
          </div>
        </a-col>
        <a-col :xs="24" :sm="12" :md="12" :lg="12" class="filter-col">
          <div class="filter-item filter-item-stack">
            <label class="filter-label">
              <a-icon type="calendar" class="action-icon" />
              {{ $t("factures.filterDateRange") }}
            </label>
            <a-range-picker
              v-model="dateRange"
              format="DD/MM/YYYY"
              class="filter-control date-range-filter"
              :placeholder="[$t('paiement.dateDebut'), $t('paiement.dateFin')]"
              @change="onDateRangeChange"
            />
          </div>
        </a-col>
      </a-row>
    </div>

    <div class="table-container">
      <invoice-table
        :invoices="rows"
        :loading="loading"
        :pagination="tablePagination"
        :show-provider="true"
        @row-click="openDrawer"
        @change="onTableChange"
      />
    </div>

    <invoice-detail-drawer
      :visible="drawerVisible"
      :depense="selected"
      @close="onDrawerClose"
      @refresh="loadInvoices"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import { fetchInvoiceList } from '@/services/api/compV3/decaissement'
import InvoiceDetailDrawer from './components/invoices/InvoiceDetailDrawer.vue'
import InvoiceTable from './components/InvoiceTable.vue'

export default {
  name: 'InvoicesTab',
  components: { InvoiceDetailDrawer, InvoiceTable },
  data() {
    return {
      loading: false,
      loadingProviders: false,
      rows: [],
      providers: [],
      drawerVisible: false,
      selected: null,
      dateRange: [],
      filters: {
        search: '',
        invoiceStatus: undefined,
        schedulingStatus: undefined,
        provider: undefined,
      },
      pagination: {
        current: 1,
        pageSize: 25,
        total: 0,
      },
      searchDebounce: null,
    }
  },
  computed: {
    tablePagination() {
      return {
        current: this.pagination.current,
        pageSize: this.pagination.pageSize,
        total: this.pagination.total,
        showSizeChanger: true,
        pageSizeOptions: ['10', '25', '50', '100'],
        showTotal: (total) => this.$t('factures.paginationTotal', { total }),
      }
    },
  },
  watch: {
    'filters.search'() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        this.pagination.current = 1
        this.loadInvoices()
      }, 400)
    },
    '$route.query': {
      immediate: true,
      deep: true,
      handler(q) {
        if (q.provider && q.invoiceNumber) {
          this.openDepenseByProviderInvoice(q.provider, q.invoiceNumber)
        }
      },
    },
  },
  created() {
    this.loadProviders()
    this.loadInvoices()
  },
  methods: {
    filterProviderOption(input, option) {
      return (
        option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
      )
    },
    buildQueryParams() {
      const params = {
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
      }
      const f = this.filters
      if (f.search && f.search.trim()) params.search = f.search.trim()
      if (f.invoiceStatus) params.invoiceStatus = f.invoiceStatus
      if (f.schedulingStatus) params.schedulingStatus = f.schedulingStatus
      if (f.provider) params.provider = f.provider
      if (this.dateRange && this.dateRange.length === 2) {
        params.dateFrom = this.dateRange[0].startOf('day').toISOString()
        params.dateTo = this.dateRange[1].endOf('day').toISOString()
      }
      return params
    },
    async loadProviders() {
      this.loadingProviders = true
      try {
        const res = await apiClient.post('/providers/filter', { query: { status: 'active' } })
        this.providers = res.data || []
      } catch {
        this.providers = []
      } finally {
        this.loadingProviders = false
      }
    },
    async loadInvoices() {
      this.loading = true
      try {
        const res = await fetchInvoiceList(this.buildQueryParams())
        const body = res.data || {}
        this.rows = body.data || []
        this.pagination.total = body.total || 0
      } catch {
        this.$message.error(this.$t('warning.connectionProblem'))
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    applyFilters() {
      this.pagination.current = 1
      this.loadInvoices()
    },
    onDateRangeChange() {
      this.applyFilters()
    },
    resetFilters() {
      this.filters = {
        search: '',
        invoiceStatus: undefined,
        schedulingStatus: undefined,
        provider: undefined,
      }
      this.dateRange = []
      this.pagination.current = 1
      this.loadInvoices()
    },
    onTableChange(pag) {
      this.pagination.current = pag.current
      this.pagination.pageSize = pag.pageSize
      this.loadInvoices()
    },
    openDrawer(record) {
      this.selected = record
      this.drawerVisible = true
    },
    onDrawerClose() {
      this.drawerVisible = false
      const q = { ...this.$route.query }
      let changed = false
      ;['provider', 'invoiceNumber', 'depenseId'].forEach((key) => {
        if (q[key]) {
          delete q[key]
          changed = true
        }
      })
      if (changed) {
        this.$router.replace({ path: this.$route.path, query: q }).catch(() => {})
      }
    },
    async openDepenseByProviderInvoice(providerId, invoiceNumber) {
      const search = String(invoiceNumber || '').trim()
      if (!providerId || !search) return
      this.filters.provider = providerId
      this.filters.search = search
      this.pagination.current = 1
      await this.loadInvoices()
      const norm = search.toLowerCase()
      const row =
        this.rows.find((r) => {
          const pid = r.provider && (r.provider._id || r.provider)
          return (
            String(pid) === String(providerId) &&
            String(r.invoiceNumber || '').trim().toLowerCase() === norm
          )
        }) || null
      if (row) {
        this.openDrawer(row)
        return
      }
      this.$message.warning(this.$t('decaissement.invoiceNotFoundForRedirect'))
    },
  },
}
</script>

<style>
@import "~@/views/compV3/common.css";
</style>
